import { useEffect, useState } from "react";
import { Check, Loader2, ThumbsDown, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi, messageFrom } from "@/lib/api";
import { money } from "@/lib/format";

/** One reason, picked or not. Pressing the picked one clears it again. */
const ReasonOption = ({ reason, selected, onToggle }) => (
  <button
    type="button"
    data-testid={`outcome-reason-${reason.key}`}
    aria-pressed={selected}
    onClick={onToggle}
    className={`flex w-full items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left text-sm outline-none transition-[border-color,background-color] duration-150 focus-visible:ring-2 focus-visible:ring-primary/30 ${
      selected
        ? "border-primary bg-[color:var(--tt-teal-mist)] font-semibold text-[color:var(--tt-teal-deep)]"
        : "border-border bg-white hover:border-[color:var(--tt-teal-line)] hover:bg-[color:var(--tt-surface-2)]"
    }`}
  >
    <span
      aria-hidden="true"
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
        selected ? "border-primary bg-primary" : "border-muted-foreground/40"
      }`}
    >
      {selected ? <Check className="h-2.5 w-2.5 text-white" /> : null}
    </span>
    {reason.label}
  </button>
);

/** A win reads differently from a loss, so the heading changes with it. */
const OutcomeHeading = ({ won, lead, stageLabel }) => (
  <DialogHeader className="text-left">
    <DialogTitle className="flex items-center gap-2">
      {won ? (
        <Trophy className="h-5 w-5 text-emerald-600" aria-hidden="true" />
      ) : (
        <ThumbsDown className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      )}
      {won ? "Nice one. Why did we win it?" : `Why did we lose ${lead.full_name}?`}
    </DialogTitle>
    <DialogDescription>
      {lead.full_name} · {lead.company} · {money(lead.quote_total)} — now marked {stageLabel}.
      Recording a reason is optional, but it's what turns this board into something you can learn
      from.
    </DialogDescription>
  </DialogHeader>
);

/**
 * Asked the moment a lead is marked Won or Lost.
 *
 * Deliberately skippable: a reason nobody wanted to give is worse than no
 * reason at all, and the board still works without one.
 */
export const OutcomeDialog = ({ prompt, onClose, onSaved }) => {
  const [choice, setChoice] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChoice(null);
    setNote("");
  }, [prompt?.lead?.quote_ref, prompt?.stage]);

  if (!prompt) return null;
  const { lead, stage, reasons, stageLabel } = prompt;

  const save = async (skip) => {
    const body = {};
    if (!skip && choice) body.outcome_reason = choice;
    if (!skip && note.trim()) body.note = note.trim();
    if (!Object.keys(body).length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const data = await adminApi.updateLead(lead.quote_ref, body);
      onSaved(data.lead);
      toast.success(`Noted — ${data.changed.join(", ")}.`);
      onClose();
    } catch (error) {
      toast.error(messageFrom(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="outcome-reason-dialog" className="bg-white sm:max-w-md">
        <OutcomeHeading won={stage === "won"} lead={lead} stageLabel={stageLabel} />

        <div className="space-y-1.5">
          {reasons.map((reason) => (
            <ReasonOption
              key={reason.key}
              reason={reason}
              selected={choice === reason.key}
              onToggle={() => setChoice(choice === reason.key ? null : reason.key)}
            />
          ))}
        </div>

        <Textarea
          data-testid="outcome-reason-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          placeholder="Anything worth remembering? (optional)"
          className="resize-none bg-white text-sm"
        />

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => save(true)}
            disabled={saving}
            data-testid="outcome-reason-skip"
          >
            Skip for now
          </Button>
          <Button
            type="button"
            onClick={() => save(false)}
            disabled={saving || (!choice && !note.trim())}
            data-testid="outcome-reason-save"
            className="min-w-[120px]"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Save reason
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
